import type IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";
import { Mic, MicOff, ShieldCheck, UserX, Video, VideoOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/primitives/badge";
import { Button } from "@/components/primitives/button";
import { cn } from "@/lib/utils";
import showDialog from "@/utils/showDialog";
import type { ModerationParticipant } from "@/zustand/CallModerationState";

type Props = {
  participant: ModerationParticipant;
  apiRef: IJitsiMeetExternalApi | null;
};

// Larger, spaced tap targets (36px, up from the old 28px) grouped and colour-coded so a
// constructive action (approve / grant) can never be mistaken for its destructive neighbour
// (mute / remove). Colour is carried on hover/focus to keep the resting row calm.
const actionButton = "size-9 rounded-md text-foreground/70 transition-colors";
const constructiveAction =
  "hover:bg-secondary/15 hover:text-secondary focus-visible:text-secondary";
const destructiveAction =
  "hover:bg-destructive/15 hover:text-destructive focus-visible:text-destructive";

/**
 * One roster row inside the moderation panel. Every action targets a REMOTE participant — the
 * panel never renders this row for the local participant (see `ModerationPanel.tsx`), since
 * muting/kicking/granting-to-yourself isn't a meaningful action here.
 *
 * LAYOUT (Orion reskin): a two-line row. Line 1 = identity (name + a moderator badge when the
 * store reports `isModerator`) with the person-level destructive action (remove) pinned to the
 * far right, away from the media toggles. Line 2 = media controls split into a labelled
 * constructive "Approve" cluster and a labelled destructive "Mute" cluster, with grant-moderator
 * kept apart as a distinct role action. Only the look changed — every `apiRef` command is intact.
 *
 * No demote/revoke control exists here by design (v1 decision) — see the comment on the
 * grant-moderator button below.
 */
export default function ModerationParticipantRow({
  participant,
  apiRef,
}: Props) {
  const { t } = useTranslation();

  function run(command: string, ...args: unknown[]) {
    apiRef?.executeCommand(command, ...args);
  }

  async function handleKick() {
    // Destructive — removes the participant from the call. Confirm (with their name) first.
    const confirmed = await showDialog(
      t("videoCall.moderation.kickTitle"),
      t("videoCall.moderation.kickConfirm", { name: participant.displayName }),
      t("videoCall.moderation.kick"),
    );
    if (!confirmed) {
      return;
    }
    // camelCase command KEY — `executeCommand` looks the name up in its command map (verified
    // against the deployed external_api.js build v=9268: `kickParticipant` → "kick-participant").
    // Passing the internal hyphenated wire-name is what caused the "Not supported command name."
    // console error the panel was throwing on every action.
    run("kickParticipant", participant.id);
  }

  return (
    <li className="rounded-md border border-transparent p-2 transition-colors hover:border-foreground/10 hover:bg-foreground/[0.03]">
      {/* Line 1 — identity + the isolated person-level destructive action. */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="truncate text-sm font-medium"
            title={participant.displayName}
          >
            {participant.displayName}
          </span>
          {participant.isModerator && (
            <Badge
              variant="secondary"
              className="shrink-0 gap-1 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide"
            >
              <ShieldCheck className="size-3" />
              {t("videoCall.moderation.moderatorBadge")}
            </Badge>
          )}
        </div>
        {/* Removing the participant is the most destructive per-person action — kept on its own,
            away from the media toggles below so it can't be hit by mistake. */}
        {/* Verified against jitsi-meet stable/jitsi-meet_11031 (API.js:940): `kick-participant`
            removes an already-joined participant from the room. NOTE: `reject-participant` is a
            DIFFERENT command (API.js:337-344) — it only denies an AV-moderation unmute request,
            not a removal — so it must never be used for the kick action. */}
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn(actionButton, destructiveAction, "shrink-0")}
          aria-label={t("videoCall.moderation.kick")}
          title={t("videoCall.moderation.kick")}
          onClick={handleKick}
        >
          <UserX className="size-4" />
        </Button>
      </div>

      {/* Line 2 — media controls, grouped and labelled so constructive vs destructive is legible. */}
      {/* "Approve to speak" (askToUnmute) and "Approve camera" (approveVideo) complete the
          moderated-mode flow: the moderator turns on "Require approval" in the panel header
          (the toggleModeration switches), participants then request to unmute/enable camera,
          and the moderator approves each here. av_moderation IS enabled on our stack
          (ENABLE_AV_MODERATION defaults true at stable/jitsi-meet_11031, which auto-creates the
          avmoderation component), so these are real actions — the earlier removal was based on
          the disproven premise that av_moderation was off.
          UX choice: the buttons are always rendered per-row (gated only on localIsModerator,
          like every other action). They are only *meaningful* while the matching moderation
          mode is on; when it's off Jitsi treats an approve as a harmless no-op. We deliberately
          keep them always-visible rather than wiring per-media conditional visibility off the
          moderation store — that adds branching for marginal benefit and the correct-command
          per-row buttons are the accepted v1 shape. Command KEYS verified in external_api.js
          v=9268 map `L`: askToUnmute → "ask-to-unmute", approveVideo → "approve-video"; both
          take a single participantId. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        {/* Constructive: approve a participant's request to unmute mic / enable camera. */}
        <div className="flex items-center gap-1">
          <span className="mr-0.5 text-[11px] font-medium uppercase tracking-wide text-foreground/40">
            {t("videoCall.moderation.approveGroup")}
          </span>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className={cn(actionButton, constructiveAction)}
            aria-label={t("videoCall.moderation.approveToSpeak")}
            title={t("videoCall.moderation.approveToSpeak")}
            onClick={() => run("askToUnmute", participant.id)}
          >
            <Mic className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className={cn(actionButton, constructiveAction)}
            aria-label={t("videoCall.moderation.approveVideo")}
            title={t("videoCall.moderation.approveVideo")}
            onClick={() => run("approveVideo", participant.id)}
          >
            <Video className="size-4" />
          </Button>
        </div>

        <div className="h-6 w-px bg-foreground/10" aria-hidden="true" />

        {/* Destructive: mute the participant's mic / camera. */}
        {/* DELIBERATE v1 LIMITATION — no per-participant "revoke/un-approve": the iframe external
            API exposes no clean per-participant revoke command. The only levers are `muteRemote-
            Participant` (mute a specific person's mic/camera, below) and toggling the whole
            moderation mode off-then-on from the panel header (which re-gates everyone). We ship mute
            + accept this limitation for v1 rather than fake a per-person revoke. */}
        <div className="flex items-center gap-1">
          <span className="mr-0.5 text-[11px] font-medium uppercase tracking-wide text-foreground/40">
            {t("videoCall.moderation.muteGroup")}
          </span>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className={cn(actionButton, destructiveAction)}
            aria-label={t("videoCall.moderation.muteAudio")}
            title={t("videoCall.moderation.muteAudio")}
            onClick={() =>
              run("muteRemoteParticipant", participant.id, "audio")
            }
          >
            <MicOff className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className={cn(actionButton, destructiveAction)}
            aria-label={t("videoCall.moderation.muteVideo")}
            title={t("videoCall.moderation.muteVideo")}
            onClick={() =>
              run("muteRemoteParticipant", participant.id, "video")
            }
          >
            <VideoOff className="size-4" />
          </Button>
        </div>

        <div className="h-6 w-px bg-foreground/10" aria-hidden="true" />

        {/* Role: promote to moderator (a distinct, constructive privilege action). */}
        {/* No demote/revoke control: only granting moderator is supported in v1. A grant lasts
            for the remainder of this call session only. Demoting needs a server-side Prosody/
            Jicofo affiliation IQ, which is out of scope for this FE-only panel (design doc §3
            "Demote gap"). */}
        {/* TODO: gate grant-moderator on guest status once the FE receives affiliation from the
            conference token (ow2-chat-be#218) — the iframe API exposes no guest marker. Until
            then grant-moderator is enabled for every participant. */}
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn(actionButton, constructiveAction)}
          aria-label={t("videoCall.moderation.grantModerator")}
          title={t("videoCall.moderation.grantModerator")}
          onClick={() => run("grantModerator", participant.id)}
        >
          <ShieldCheck className="size-4" />
        </Button>
      </div>
    </li>
  );
}
